import React, { useState, useEffect } from 'lib/react';
import {
  EVGrid,
} from 'lib/components';
import { useTranslation } from 'lib/translation';
import getResourceFileService from 'services/getResourceFile.service';
import { convertFileToBase64 } from 'utils/app.utils';
import {
  FILE_TYPES, FILE_FORMATS, CUSTOM_COVER_DEFAULT_LAYOUT,
} from 'constants/AppConstants';
import uploadResourceFileService from 'services/uploadResourceFile.service';
import RetryScreen from 'commonComponents/retry-screen';
import { getPropertyValues } from 'features/orderPreferences/OrderPreferences.utils';
import clsx from 'lib/clsx';
import PropTypes from 'prop-types';
import Typography from 'commonComponents/typography';
import PreviewCoverPage from './components/previewCoverPage/PreviewCoverPage.component';
import CustomCoverStyles from './CustomCoverStyles';
import CompanyInformation from './components/CompanyInformation.component';
import ContactInformation from './components/ContactInformation.component';
import PageLayout from './components/PageLayout.component';

function CustomCover({
  orderPreferences,
  inputFieldValues,
  handlePropertyChange,
}) {
  const classes = CustomCoverStyles();
  const { t } = useTranslation();

  const {
    isAdmin,
    reportOptionsCustomCover,
  } = orderPreferences;

  const [logoFile, setLogoFile] = useState({
    data: null,
    name: null,
  });

  const [uploadingLogoFile, setUploadingLogoFile] = useState(false);
  const [uploadLogoFileError, setUploadLogoFileError] = useState(false);
  const [getLogoFileError, setGetLogoFileError] = useState(true);

  const fetchLogoFile = () => {
    setGetLogoFileError(false);
    const companyLogoFileId = getPropertyValues(orderPreferences, inputFieldValues, 'reportOptionsCoverCompanyLogo')?.value;
    if (companyLogoFileId) {
      getResourceFileService(companyLogoFileId)
        .then(({ ok, json }) => (
          ok
            ? setLogoFile({
              data: `data:image/${json.fileDescription?.split('.').slice(-1)[0]};base64,${json.fileData}`,
              name: json.fileDescription,
            })
            : setGetLogoFileError(true)
        ))
        .catch(() => setGetLogoFileError(true));
    }
  };

  useEffect(
    () => fetchLogoFile(),
    [orderPreferences.reportOptionsCoverCompanyLogo],
  );

  const handleLogoFileUpload = async (event) => {
    const { target: { files: [file] } } = event;
    setUploadingLogoFile(true);
    setUploadLogoFileError(false);
    setLogoFile({ name: file.name });
    try {
      const { ok, json } = await uploadResourceFileService({
        fileType: FILE_TYPES.CustomerLogo,
        fileFormat: FILE_FORMATS[file.type.replace('image/', '').toUpperCase()],
        validateFile: true,
        fileName: file.name,
        fileData: file,
      });

      if (!ok) throw new Error('Error uploading file');

      const fileData = await convertFileToBase64(file);
      const { fileId } = json;

      setLogoFile({ data: fileData, name: file.name });
      handlePropertyChange('reportOptionsCoverCompanyLogo', 'value', fileId);
    } catch (error) {
      setUploadLogoFileError(true);
    }
    setUploadingLogoFile(false);
  };

  const handleCoverColorChange = ({ hex: value }) => handlePropertyChange('reportOptionsCoverPrimaryColor', 'value', value);

  const handleLayoutChange = ({ target: { value } }) => handlePropertyChange('reportOptionsCustomCover', 'value', Number(value));

  const showCoverPageSettings = isAdmin || reportOptionsCustomCover?.allowSubAccounts;

  return (
    <>
      <Typography variant="h3-600" fontColor="charcoal-900">
        {t('order_preferences__cover_page')}
      </Typography>
      {showCoverPageSettings && (
        <>
          <CompanyInformation
            orderPreferences={orderPreferences}
            inputFieldValues={inputFieldValues}
            handlePropertyChange={handlePropertyChange}
          />
          <ContactInformation
            orderPreferences={orderPreferences}
            inputFieldValues={inputFieldValues}
            handlePropertyChange={handlePropertyChange}
          />
        </>
      )}
      <EVGrid className={clsx(classes.coverPagePreviewContainer, showCoverPageSettings || 'settingsDisabled')}>
        {getLogoFileError
          ? (
            <RetryScreen
              retryError="There was an error previewing the Custom Cover page"
              handleRetryButtonClick={fetchLogoFile}
              style={{ boxShadow: 'none' }}
            />
          ) : (
            <>
              {showCoverPageSettings && (
                <PageLayout
                  logoFile={logoFile}
                  coverColor={getPropertyValues(orderPreferences, inputFieldValues, 'reportOptionsCoverPrimaryColor')?.value}
                  selectedLayout={getPropertyValues(orderPreferences, inputFieldValues, 'reportOptionsCustomCover')?.value || CUSTOM_COVER_DEFAULT_LAYOUT}
                  handleCoverColorChange={handleCoverColorChange}
                  handleLayoutChange={handleLayoutChange}
                  handleLogoFileUpload={handleLogoFileUpload}
                  uploadingLogoFile={uploadingLogoFile}
                  uploadLogoFileError={uploadLogoFileError}
                />
              )}
              <PreviewCoverPage
                logoFileData={logoFile.data}
                orderPreferences={orderPreferences}
                inputFieldValues={inputFieldValues}
              />
            </>
          )}
      </EVGrid>
    </>
  );
}

CustomCover.propTypes = {
  orderPreferences: PropTypes.shape().isRequired,
  inputFieldValues: PropTypes.shape().isRequired,
  handlePropertyChange: PropTypes.func.isRequired,
};

export default CustomCover;
